import React, { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DownloadOutlined,
  EnvironmentOutlined,
  FileExcelOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import employeeService, {
  employeeKeys,
  type EmployeeListParams,
  type EmployeeRecord,
  type LocationAssignmentResult,
} from '@/services/employee.service';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import departmentService from '@/services/department.service';
import { useAuthStore } from '@/store/authStore';

const { Text } = Typography;

const UNASSIGNED = 'none';
const REMOVE_LOCATION = '__remove__';
const EXITED = new Set(['Resigned', 'Terminated']);

const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const EmployeeLocationAssignmentPage: React.FC = () => {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const canEdit =
    Boolean(user?.is_super_admin) || Boolean(user?.permissions?.includes('hr.employee.edit'));

  const [locationFilter, setLocationFilter] = useState<string | undefined>(
    searchParams.get('location') || undefined
  );
  const [departmentFilter, setDepartmentFilter] = useState<string>();
  const [statusFilter, setStatusFilter] = useState<string>();
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
  const [target, setTarget] = useState<string>();
  const [effectiveDate, setEffectiveDate] = useState<Dayjs>(dayjs());
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<LocationAssignmentResult | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const listParams: EmployeeListParams = useMemo(
    () => ({
      location_id: locationFilter,
      department_id: departmentFilter,
      employment_status: statusFilter,
      search: search.trim() || undefined,
    }),
    [locationFilter, departmentFilter, statusFilter, search]
  );

  const { data: employees = [], isLoading } = useQuery({
    queryKey: employeeKeys.list(listParams),
    queryFn: () => employeeService.list(listParams),
  });
  const rows = useMemo(
    () => employees.filter((e) => !EXITED.has(String(e.employment_status))),
    [employees]
  );

  const { data: locations = [] } = useQuery({
    queryKey: hrmsKeys.locations(),
    queryFn: () => hrmsService.listLocations(),
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['departments', 'selection', 'employees-list'],
    queryFn: () => departmentService.getForSelection(),
    staleTime: 5 * 60 * 1000,
  });

  const locationLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of locations) m.set(l.id, `${l.location_code} — ${l.location_name}`);
    return m;
  }, [locations]);
  const deptNameById = useMemo(
    () => new Map(departments.map((d) => [d.id, d.department_name])),
    [departments]
  );

  const locationFilterOptions = [
    { value: UNASSIGNED, label: 'No location assigned' },
    ...locations.map((l: any) => ({
      value: l.id,
      label: `${l.location_code} — ${l.location_name}${l.is_active === false ? ' (inactive)' : ''}`,
    })),
  ];
  const targetOptions = [
    ...locations
      .filter((l: any) => l.is_active !== false)
      .map((l: any) => ({ value: l.id, label: `${l.location_code} — ${l.location_name}` })),
    { value: REMOVE_LOCATION, label: 'Remove location (unassign)' },
  ];

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    void queryClient.invalidateQueries({ queryKey: hrmsKeys.locations() });
  };

  const assignMutation = useMutation({
    mutationFn: () =>
      employeeService.assignLocation({
        employee_ids: selectedIds.map(String),
        location_id: target === REMOVE_LOCATION ? null : target!,
        effective_date: effectiveDate.format('YYYY-MM-DD'),
        reason: reason.trim() || undefined,
      }),
    onSuccess: (r) => {
      setResult(r);
      setSelectedIds([]);
      setReason('');
      refresh();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Assignment failed'),
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => employeeService.importLocationAssignments(file),
    onSuccess: (r) => {
      setResult(r);
      refresh();
    },
    onError: (e: any) => {
      const errors: string[] | undefined = e?.response?.data?.data?.errors;
      if (errors?.length) setImportErrors(errors);
      else message.error(e?.response?.data?.message || e?.message || 'Import failed');
    },
  });

  const confirmAssign = () => {
    const targetLabel =
      target === REMOVE_LOCATION ? 'no location' : locationLabelById.get(target!) || 'the location';
    modal.confirm({
      title: `Assign ${selectedIds.length} employee(s) to ${targetLabel}?`,
      content: `Effective ${effectiveDate.format('DD MMM YYYY')}. Each change is saved as a Transfer in the employee's lifecycle history.`,
      okText: 'Assign',
      onOk: () => assignMutation.mutateAsync(),
    });
  };

  const handleTemplate = async () => {
    try {
      saveBlob(
        await employeeService.downloadLocationAssignmentTemplateBlob(),
        'employee-location-assignment-template.xlsx'
      );
    } catch {
      message.error('Template download failed');
    }
  };

  const handleExport = async () => {
    try {
      saveBlob(
        await employeeService.exportLocationAssignmentsBlob(listParams),
        `employee-locations-${dayjs().format('YYYY-MM-DD')}.xlsx`
      );
    } catch {
      message.error('Export failed');
    }
  };

  const columns: ColumnsType<EmployeeRecord> = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{`${r.first_name} ${r.last_name}`}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.employee_code}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Department',
      key: 'department',
      width: 200,
      render: (_, r) => (r.department_id ? deptNameById.get(r.department_id) || '—' : '—'),
    },
    {
      title: 'Job title',
      dataIndex: 'job_title',
      width: 200,
      ellipsis: true,
      render: (v: string) => v || '—',
    },
    {
      title: 'Status',
      dataIndex: 'employment_status',
      width: 120,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Current location',
      key: 'location',
      width: 260,
      render: (_, r) =>
        r.location_id ? (
          <Space size={4}>
            <EnvironmentOutlined />
            {locationLabelById.get(r.location_id) || r.location_id}
          </Space>
        ) : (
          <Tag color="orange">Not assigned</Tag>
        ),
    },
  ];

  const unassignedCount = rows.filter((r) => !r.location_id).length;

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Employee location assignment"
          subtitle="Bulk assign or reassign employees to work locations (used for mobile check-in geofencing)"
          primaryActions={
            <Space wrap>
              <Button icon={<FileExcelOutlined />} onClick={handleTemplate}>
                Import template
              </Button>
              {canEdit && (
                <Button
                  icon={<UploadOutlined />}
                  loading={importMutation.isPending}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Import
                </Button>
              )}
              <Button icon={<DownloadOutlined />} onClick={handleExport}>
                Export
              </Button>
            </Space>
          }
        />
      }
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) importMutation.mutate(file);
        }}
      />

      {canEdit && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap align="center">
            <Text strong>
              {selectedIds.length
                ? `${selectedIds.length} employee(s) selected`
                : 'Select employees below, then'}
            </Text>
            <Select
              placeholder="New location"
              style={{ width: 300 }}
              showSearch
              optionFilterProp="label"
              options={targetOptions}
              value={target}
              onChange={setTarget}
            />
            <DatePicker
              value={effectiveDate}
              onChange={(d) => setEffectiveDate(d || dayjs())}
              allowClear={false}
              format="DD MMM YYYY"
              disabledDate={(d) => d.isAfter(dayjs(), 'day')}
            />
            <Input
              placeholder="Reason (optional)"
              style={{ width: 260 }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
            />
            <Button
              type="primary"
              disabled={!selectedIds.length || !target}
              loading={assignMutation.isPending}
              onClick={confirmAssign}
            >
              Assign location
            </Button>
          </Space>
        </Card>
      )}

      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="Search name, code, email"
            allowClear
            prefix={<SearchOutlined />}
            style={{ width: 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            placeholder="Current location"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 280 }}
            options={locationFilterOptions}
            value={locationFilter}
            onChange={(v) => {
              setLocationFilter(v);
              setSelectedIds([]);
            }}
          />
          <Select
            placeholder="Department"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 220 }}
            options={departments.map((d) => ({ value: d.id, label: d.department_name }))}
            value={departmentFilter}
            onChange={setDepartmentFilter}
          />
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 160 }}
            options={['Active', 'Onboarding', 'On Leave', 'Suspended', 'Inactive'].map((s) => ({
              value: s,
              label: s,
            }))}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          {unassignedCount > 0 && !locationFilter && (
            <Button type="link" onClick={() => setLocationFilter(UNASSIGNED)}>
              {unassignedCount} without a location
            </Button>
          )}
        </Space>
        <Table<EmployeeRecord>
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={rows}
          rowSelection={
            canEdit
              ? {
                  selectedRowKeys: selectedIds,
                  onChange: setSelectedIds,
                  preserveSelectedRowKeys: true,
                }
              : undefined
          }
          pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'] }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No employees" /> }}
        />
      </Card>

      <Modal
        title="Location assignment done"
        open={Boolean(result)}
        onCancel={() => setResult(null)}
        footer={<Button onClick={() => setResult(null)}>Close</Button>}
      >
        {result && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>
              {result.changed} employee(s) reassigned, {result.unchanged} already at that location.
            </Text>
            {result.skipped.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`${result.skipped.length} skipped`}
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {result.skipped.map((s) => (
                      <li key={s.employee_code}>
                        {s.employee_code}: {s.reason}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
            {result.pending_profile_requests.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message="Pending profile change requests also change the location"
                description={`If approved, these will overwrite the location just assigned: ${result.pending_profile_requests.join(', ')}. Review them in Profile change requests.`}
              />
            )}
          </Space>
        )}
      </Modal>

      <Modal
        title="Import failed — nothing was imported"
        open={importErrors.length > 0}
        onCancel={() => setImportErrors([])}
        footer={<Button onClick={() => setImportErrors([])}>Close</Button>}
      >
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Fix these rows in the file and upload it again."
        />
        <ul style={{ maxHeight: 320, overflow: 'auto', paddingLeft: 18 }}>
          {importErrors.map((err) => (
            <li key={err}>{err}</li>
          ))}
        </ul>
      </Modal>
    </WorkspaceLayout>
  );
};

export default EmployeeLocationAssignmentPage;
